import React, { useEffect, useRef, useState } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_taf-studios/artifacts/ama7gpm7_Asset%2011%404x%20%281%29.png';

export const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const startTimeRef = useRef(Date.now());
  const logoLoadedRef = useRef(false);

  useEffect(() => {
    // Preload logo image first
    const logoImg = new Image();
    logoImg.onload = () => {
      logoLoadedRef.current = true;
      setIsReady(true);
    };
    logoImg.src = LOGO_URL;

    // Progress animation - smooth counting
    let currentProgress = 0;
    const minTime = 3000; // 3 seconds minimum
    const maxTime = 5000; // 5 seconds maximum
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      
      // Calculate target based on time and load state
      let target;
      if (elapsed < minTime) {
        // During min time: progress to 80% max
        target = (elapsed / minTime) * 80;
      } else if (document.readyState === 'complete' && logoLoadedRef.current) {
        // After min time + loaded: go to 100%
        target = 100;
      } else {
        // After min time but still loading: slowly go to 95%
        target = 80 + ((elapsed - minTime) / (maxTime - minTime)) * 15;
      }
      
      // Smooth increment - move towards target
      const diff = target - currentProgress;
      const increment = Math.max(0.5, diff * 0.1); // Smooth easing
      currentProgress = Math.min(currentProgress + increment, target, 100);
      
      setProgress(Math.round(currentProgress));
      
      // Check completion
      if (currentProgress >= 100) {
        clearInterval(progressInterval);
        setTimeout(() => {
          setIsExiting(true);
          setTimeout(() => {
            if (onComplete) onComplete();
          }, 600);
        }, 300);
      }
      
      // Force complete at max time
      if (elapsed >= maxTime && currentProgress < 100) {
        currentProgress = 100;
        setProgress(100);
      }
    }, 50); // Update every 50ms for smooth counting

    return () => {
      clearInterval(progressInterval);
    };
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden ${isExiting ? 'loading-exit' : ''}`}
      style={{
        background: 'linear-gradient(135deg, #080400 0%, #150a00 50%, #080400 100%)'
      }}
    >
      {/* Radial glow */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(249, 115, 22, 0.1) 0%, transparent 50%)'
        }}
      />

      {/* Center content - appears together */}
      <div className={`relative z-10 flex flex-col items-center content-ready`}>
        
        {/* Logo wrapper with neon border */}
        <div className="relative p-5 sm:p-7 mb-6">
          {/* Neon border traces */}
          <div className="absolute inset-0 neon-container">
            <div className="neon-trace neon-top" />
            <div className="neon-trace neon-right" />
            <div className="neon-trace neon-bottom" />
            <div className="neon-trace neon-left" />
            {/* Corner glows */}
            <div className="corner-glow tl" />
            <div className="corner-glow tr" />
            <div className="corner-glow bl" />
            <div className="corner-glow br" />
          </div>

          {/* Logo */}
          <img 
            src={LOGO_URL}
            alt="TAF WRLD"
            className="relative z-10 w-[180px] sm:w-[260px] md:w-[300px] h-auto logo-pulse"
            style={{
              filter: 'drop-shadow(0 0 25px rgba(249, 115, 22, 0.5))'
            }}
          />
        </div>

        {/* Progress bar */}
        <div className="w-40 sm:w-52 h-1 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #f97316, #fbbf24, #f97316)'
            }}
          />
        </div>

        {/* Progress text */}
        <p className="text-orange-400/50 text-xs tracking-widest mt-3 font-mono">
          {Math.round(progress)}%
        </p>
      </div>

      {/* Corner brackets */}
      <div className="absolute top-6 left-6 w-12 h-12 border-l-2 border-t-2 border-orange-500/30" />
      <div className="absolute top-6 right-6 w-12 h-12 border-r-2 border-t-2 border-orange-500/30" />
      <div className="absolute bottom-6 left-6 w-12 h-12 border-l-2 border-b-2 border-orange-500/30" />
      <div className="absolute bottom-6 right-6 w-12 h-12 border-r-2 border-b-2 border-orange-500/30" />

      <style>{`
        /* Content appears together */
        .content-loading {
          opacity: 0;
          transform: scale(0.95);
        }
        .content-ready {
          opacity: 1;
          transform: scale(1);
          transition: opacity 0.4s ease-out, transform 0.4s ease-out;
        }

        /* Exit animation - zoom out + blur */
        .loading-exit {
          animation: exitZoom 0.6s ease-in forwards;
        }
        @keyframes exitZoom {
          0% {
            opacity: 1;
            transform: scale(1);
            filter: blur(0px);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.1);
            filter: blur(2px);
          }
          100% {
            opacity: 0;
            transform: scale(0.8);
            filter: blur(10px);
          }
        }

        /* Logo pulse */
        .logo-pulse {
          animation: logoPulse 2s ease-in-out infinite;
        }
        @keyframes logoPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }

        /* Neon traces */
        .neon-container {
          pointer-events: none;
        }
        .neon-trace {
          position: absolute;
          background: linear-gradient(90deg, transparent 0%, rgba(249, 115, 22, 0.9) 50%, transparent 100%);
          filter: blur(1px);
          box-shadow: 0 0 15px rgba(249, 115, 22, 0.7), 0 0 30px rgba(249, 115, 22, 0.3);
        }
        .neon-top {
          top: 0; left: 0; height: 2px; width: 50%;
          animation: traceRight 2.5s linear infinite;
        }
        .neon-right {
          top: 0; right: 0; width: 2px; height: 50%;
          background: linear-gradient(180deg, transparent 0%, rgba(251, 146, 60, 0.9) 50%, transparent 100%);
          animation: traceDown 2.5s linear infinite;
          animation-delay: 0.625s;
        }
        .neon-bottom {
          bottom: 0; right: 0; height: 2px; width: 50%;
          animation: traceLeft 2.5s linear infinite;
          animation-delay: 1.25s;
        }
        .neon-left {
          bottom: 0; left: 0; width: 2px; height: 50%;
          background: linear-gradient(180deg, transparent 0%, rgba(251, 146, 60, 0.9) 50%, transparent 100%);
          animation: traceUp 2.5s linear infinite;
          animation-delay: 1.875s;
        }

        @keyframes traceRight {
          0% { left: -50%; }
          100% { left: 100%; }
        }
        @keyframes traceDown {
          0% { top: -50%; }
          100% { top: 100%; }
        }
        @keyframes traceLeft {
          0% { right: -50%; }
          100% { right: 100%; }
        }
        @keyframes traceUp {
          0% { bottom: -50%; }
          100% { bottom: 100%; }
        }

        /* Corner glows */
        .corner-glow {
          position: absolute;
          width: 6px;
          height: 6px;
          background: rgba(249, 115, 22, 0.8);
          border-radius: 50%;
          filter: blur(2px);
          box-shadow: 0 0 10px rgba(249, 115, 22, 0.8);
          animation: cornerPulse 1.5s ease-in-out infinite;
        }
        .corner-glow.tl { top: -3px; left: -3px; }
        .corner-glow.tr { top: -3px; right: -3px; animation-delay: 0.375s; }
        .corner-glow.br { bottom: -3px; right: -3px; animation-delay: 0.75s; }
        .corner-glow.bl { bottom: -3px; left: -3px; animation-delay: 1.125s; }

        @keyframes cornerPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
      `}</style>
    </div>
  );
};
